import { api, http } from "./client";

const IDP_ORIGIN = process.env.REACT_APP_IDP_ORIGIN || "";

export type ChatParticipant = {
  id: number;
  userId: number | null;
  phone: string | null;
  displayName: string | null;
  status: "active" | "invited";
  role: "owner" | "member";
  isMe: boolean;
  joinedAt: string | null;
  lastSeenAt?: string | null;
  preferences?: ChatPreferences;
};

export type ChatPreferences = {
  isMuted?: boolean;
  mutedUntil?: string | null;
  chatTheme?: string | null;
  compactMode?: boolean;
  autoDownloadPhotos?: boolean;
  autoDownloadVideos?: boolean;
  autoDownloadFiles?: boolean;
  autoDownloadVoice?: boolean;
  isPinned?: boolean;
  pinnedAt?: string | null;
  isImportant?: boolean;
  importantAt?: string | null;
};

export type ChatAttachment = {
  id: number;
  messageId?: number | null;
  chatId?: number | null;
  authorUserId?: number | null;
  text?: string;
  kind: "image" | "video" | "file" | "voice";
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  width?: number | null;
  height?: number | null;
  durationSec?: number | null;
  createdAt: string;
};

export type ChatMessage = {
  id: number;
  chatId: number;
  authorUserId: number | null;
  text: string;
  type: "text" | "media" | "file" | "voice" | "system";
  attachments: ChatAttachment[];
  links: string[];
  createdAt: string;
  updatedAt?: string | null;
  editedAt: string | null;
  deletedAt?: string | null;
  isDeleted?: boolean;
  replyTo?: ChatReplyPreview | null;
  forwardedFrom?: ChatForwardMeta | null;
  reactions?: ChatReaction[];
  isPinned?: boolean;
  isImportantForMe?: boolean;
};

export type ChatReplyPreview = {
  id: number;
  authorUserId: number | null;
  text: string;
  type: ChatMessage["type"];
  attachmentsCount: number;
  isDeleted: boolean;
};

export type ChatForwardMeta = {
  chatId: number | null;
  messageId: number | null;
  authorUserId: number | null;
  authorPhone?: string | null;
  forwardedAt?: string | null;
};

export type ChatReaction = {
  reaction: string;
  count: number;
  reactedByMe: boolean;
};

export type ChatPinnedMessage = {
  id: number;
  messageId: number | null;
  pinnedByUserId: number | null;
  createdAt: string;
  message: ChatMessage | null;
};

export type ChatReadState = {
  userId: number | null;
  lastReadMessageId: number | null;
  readAt: string;
  updatedAt: string;
};

export type ChatTypingState = {
  userId: number | null;
  isTyping: boolean;
  updatedAt: string;
};

export type ChatItem = {
  id: number;
  title: string | null;
  isGroup: boolean;
  createdAt: string;
  updatedAt: string;
  participants: ChatParticipant[];
  lastMessage: ChatMessage | null;
  unreadCount: number;
  isPinned?: boolean;
  pinnedAt?: string | null;
  isImportant?: boolean;
  importantAt?: string | null;
  preview?: string | null;
  mediaCounters?: {
    media?: number;
    files?: number;
    links?: number;
    voice?: number;
  };
};

export type ChatSettings = {
  chatId: number;
  title: string | null;
  isGroup: boolean;
  participantCount: number;
  preferences: ChatPreferences;
  pinnedMessages?: ChatPinnedMessage[];
  mediaCounters?: {
    media?: number;
    files?: number;
    links?: number;
    voice?: number;
  };
  participants?: ChatParticipant[];
};

type ChatListResponse = {
  items: ChatItem[];
};

type MessageListResponse = {
  items: ChatMessage[];
  hasMore: boolean;
};

export const chatApi = {
  async listChats(limit = 40, offset = 0): Promise<ChatItem[]> {
    const data = await api.get<ChatListResponse>("/api/chats", { limit, offset });
    return data?.items ?? [];
  },

  async getChat(chatId: number): Promise<ChatItem> {
    return await api.get<ChatItem>(`/api/chats/${chatId}`);
  },

  async createChat(payload: {
    title?: string;
    isGroup?: boolean;
    participantPhones: string[];
  }): Promise<ChatItem> {
    return await api.post<ChatItem>("/api/chats", payload);
  },

  async inviteByPhone(chatId: number, phone: string, displayName?: string): Promise<ChatParticipant> {
    const data = await api.post<{ participant: ChatParticipant }>(`/api/chats/${chatId}/invite`, {
      phone,
      displayName,
    });
    return data.participant;
  },

  async listMessages(chatId: number, params?: { limit?: number; beforeId?: number; aroundId?: number }): Promise<MessageListResponse> {
    return await api.get<MessageListResponse>(`/api/chats/${chatId}/messages`, params);
  },

  async searchMessages(chatId: number, params?: { q?: string; bucket?: string; authorUserId?: number; limit?: number }): Promise<ChatMessage[]> {
    const data = await api.get<{ items: ChatMessage[] }>(`/api/chats/${chatId}/messages/search`, params);
    return data?.items ?? [];
  },

  async sendMessage(chatId: number, text: string, options?: { replyToMessageId?: number | null }): Promise<ChatMessage> {
    const data = await api.post<{ message: ChatMessage }>(`/api/chats/${chatId}/messages`, {
      text,
      replyToMessageId: options?.replyToMessageId || undefined,
    });
    return data.message;
  },

  async editMessage(chatId: number, messageId: number, text: string): Promise<ChatMessage> {
    const data = await api.put<{ message: ChatMessage }>(`/api/chats/${chatId}/messages/${messageId}`, { text });
    return data.message;
  },

  async deleteMessage(chatId: number, messageId: number): Promise<ChatMessage> {
    const data = await api.delete<{ message: ChatMessage }>(`/api/chats/${chatId}/messages/${messageId}`);
    return data.message;
  },

  async uploadFiles(
    chatId: number,
    files: File[] | FileList,
    options?: { text?: string; kind?: string; durationSec?: number | null; replyToMessageId?: number | null }
  ): Promise<ChatMessage> {
    const formData = new FormData();
    Array.prototype.slice.call(files || []).forEach((file: File) => {
      formData.append("files[]", file);
    });
    if (options?.text) formData.append("text", options.text);
    if (options?.kind) formData.append("kind", options.kind);
    if (options?.durationSec != null) formData.append("durationSec", String(options.durationSec));
    if (options?.replyToMessageId) formData.append("replyToMessageId", String(options.replyToMessageId));

    const res = await http.post<{ message: ChatMessage }>(`/api/chats/${chatId}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.message;
  },

  async getChatSettings(chatId: number): Promise<ChatSettings> {
    const data = await api.get<{ settings: ChatSettings }>(`/api/chats/${chatId}/settings`);
    return data.settings;
  },

  async updateChatSettings(chatId: number, payload: ChatPreferences & { title?: string | null }): Promise<{ settings: ChatSettings; chat?: ChatItem }> {
    return await api.put<{ settings: ChatSettings; chat?: ChatItem }>(`/api/chats/${chatId}/settings`, payload);
  },

  async getChatMedia(chatId: number, bucket = "media", limit = 60, offset = 0): Promise<Array<ChatAttachment | ChatMessage>> {
    const data = await api.get<{ items: Array<ChatAttachment | ChatMessage> }>(`/api/chats/${chatId}/media`, {
      bucket,
      limit,
      offset,
    });
    return data?.items ?? [];
  },

  async listPins(chatId: number): Promise<ChatPinnedMessage[]> {
    const data = await api.get<{ items: ChatPinnedMessage[] }>(`/api/chats/${chatId}/pins`);
    return data?.items ?? [];
  },

  async pinMessage(chatId: number, messageId: number): Promise<ChatPinnedMessage[]> {
    const data = await api.post<{ items: ChatPinnedMessage[] }>(`/api/chats/${chatId}/pins`, { messageId });
    return data?.items ?? [];
  },

  async unpinMessage(chatId: number, messageId: number): Promise<ChatPinnedMessage[]> {
    const data = await api.delete<{ items: ChatPinnedMessage[] }>(`/api/chats/${chatId}/pins/${messageId}`);
    return data?.items ?? [];
  },

  async reactToMessage(chatId: number, messageId: number, reaction: string): Promise<ChatMessage> {
    const data = await api.post<{ message: ChatMessage }>(`/api/chats/${chatId}/messages/${messageId}/reactions`, { reaction });
    return data.message;
  },

  async deleteReaction(chatId: number, messageId: number): Promise<ChatMessage> {
    const data = await api.delete<{ message: ChatMessage }>(`/api/chats/${chatId}/messages/${messageId}/reactions`);
    return data.message;
  },

  async markMessageImportant(chatId: number, messageId: number): Promise<ChatMessage> {
    const data = await api.post<{ message: ChatMessage }>(`/api/chats/${chatId}/messages/${messageId}/important`);
    return data.message;
  },

  async unmarkMessageImportant(chatId: number, messageId: number): Promise<ChatMessage> {
    const data = await api.delete<{ message: ChatMessage }>(`/api/chats/${chatId}/messages/${messageId}/important`);
    return data.message;
  },

  async forwardMessage(targetChatId: number, sourceMessageId: number): Promise<ChatMessage> {
    const data = await api.post<{ message: ChatMessage }>(`/api/chats/${targetChatId}/messages/forward`, { sourceMessageId });
    return data.message;
  },

  async markRead(chatId: number, messageId: number): Promise<ChatReadState> {
    const data = await api.post<{ readState: ChatReadState }>(`/api/chats/${chatId}/read`, { messageId });
    return data.readState;
  },

  async setTyping(chatId: number, isTyping: boolean): Promise<ChatTypingState> {
    const data = await api.post<{ typingState: ChatTypingState }>(`/api/chats/${chatId}/typing`, { isTyping });
    return data.typingState;
  },

  resolveAssetUrl(path?: string | null): string {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const normalized = IDP_ORIGIN.replace(/\/$/, "");
    return `${normalized}${path.charAt(0) === "/" ? path : `/${path}`}`;
  },
};
